import { useState } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import Close from "../Buttons/Close";
import AddButton from "../Buttons/AddButton";
import RemoveButton from "../Buttons/RemoveButton";
import { useCfmContext } from "../../Context/CfmContext";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import useDraggableDialog from "./Utils/UseDraggableDialog";
import Input1 from "../Inputs/Input1";
import { ReactComponent as TooltipIcon } from "../../../../../assets/icons/question-mark-tooltip.svg";
import TooltipBox from "../Tooltips/TooltipBox";
import InputSmall from "../Inputs/InputSmall";
dayjs.extend(customParseFormat);

const defaultRow = {
  year: "",
  rate: "",
};

/**
 * MarketAssumptionsDialogBox component displays a draggable dialog for configuring market assumptions,
 * including inflation rate and stress testing (potential drop risk) parameters.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onClose - Callback function to close the dialog box.
 *
 * @returns {JSX.Element} The rendered MarketAssumptionsDialogBox component.
 *
 * @example
 * <MarketAssumptionsDialogBox onClose={() => setShowDialog(false)} />
 *
 * @description
 * - Allows users to set a global inflation rate, which is applied to all relevant figures.
 * - Provides a section for stress testing by adding, editing, or removing drop risk scenarios (year and rate).
 * - Utilizes tooltips to explain the meaning of inflation and stress testing.
 * - Validates user input for year (must be 4 digits) and rate (must be a valid number).
 * - Uses context for state management and dispatching updates.
 * - Supports drag-and-drop repositioning of the dialog.
 */
const MarketAssumptionsDialogBox = ({ onClose }) => {
  const { state, dispatch } = useCfmContext();
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [errors, setErrors] = useState({});
  const dropRiskData = state.dropRiskData ?? [];
  const { ref, position, handleMouseDown } = useDraggableDialog();
  const [hovered, setHovered] = useState(false);
  const [hoveredRate, setHoveredRate] = useState(false);

  /**
   * Handles changes to input fields by updating the corresponding value in the input row state
   * and resetting any error state for the changed field.
   *
   * @param {string} field - The name of the input field being changed.
   * @param {*} value - The new value for the input field.
   */
  const handleInputChange = (field, value) => {
    setInputRow((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  /**
   * Handles the addition of a new market assumption row.
   * 
   * Validates the input fields for year and rate. If validation fails, sets error states.
   * If validation passes, updates the dropRiskData state with the new row, resets the input row,
   * and clears any existing errors.
   *
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    const { year, rate } = inputRow;
    const isYearValid = /^\d{4}$/.test(year.trim());
    const isRateValid = !isNaN(Number(rate)) && rate.trim() !== "";

    const newErrors = {
      year: !isYearValid,
      rate: !isRateValid,
    };

    if (!isYearValid || !isRateValid) {
      setErrors(newErrors);
      return;
    }

    const updated = [...(dropRiskData || []), { ...inputRow }];

    dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
    setInputRow({ ...defaultRow });
    setErrors({});
  };

  /**
   * Removes an item from the dropRiskData array at the specified index and updates the state.
   *
   * @function
   * @param {number} index - The index of the item to remove from the dropRiskData array.
   * @returns {void}
   */
  const handleRemove = (index) => {
    const updated = dropRiskData.filter((_, i) => i !== index);
    dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
  };

  /**
   * Synchronizes the inflation value across all rows in the details state.
   * 
   * Dispatches an action to update the global inflation value, then updates each row's
   * inflation property in the details array to match the new value, and dispatches the updated array.
   *
   * @param {number} newInflation - The new inflation value to be applied to all rows.
   */
  const syncInflationAcrossRows = (newInflation) => {
    dispatch({ type: "UPDATE_INFLATION", payload: newInflation });
    const updated = state.details.map((row) => ({
      ...row,
      inflation: newInflation,
    }));
    dispatch({ type: "UPDATE_DETAILS", payload: updated });
  };

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Market Assumptions</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>

      <div className="personaldetails-dialogbox-inputrow">
        <div className="personaldetails-dialogbox-inputColumn">
          <div className="dialog-tooltip-row-inputs">
            <div className="Title">Inflation</div>
            <div
              style={{ position: "relative", display: "inline-block" }}
              onMouseEnter={() => setHoveredRate(true)}
              onMouseLeave={() => setHoveredRate(false)}
            >
              <TooltipIcon />
              {hoveredRate && (
                <TooltipBox>
                  <div className="tooltip-content">
                    An inflation rate of {state.inflation || 0}% per year has
                    been applied to all the figures used in this modeller,
                    including the State Pension. The only exception applies to
                    one-off contributions or withdrawals, which will be modelled
                    using the exact amounts you request.
                  </div>
                </TooltipBox>
              )}
            </div>
          </div>

          <div className="dialog-row-inputs">
            <Input1
              label="Inflation Rate"
              value={state.inflation}
              onChange={(val) => syncInflationAcrossRows(val)}
              per
              type="number"
            />
          </div>
        </div>

        <div className="personaldetails-dialogbox-inputColumn">
          <div className="dialog-tooltip-row-inputs">
            <div className="Title">Stress Testing</div>
            <div
              style={{ position: "relative", display: "inline-block" }}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              <TooltipIcon />
              {hovered && (
                <TooltipBox>
                  <div
                    className="tooltip-content"
                    style={{ maxWidth: "250px" }}
                  >
                    Stress Testing models a market drop (optional shocks), then
                    recalculates balances and withdrawals to show plan
                    resilience.
                  </div>
                </TooltipBox>
              )}
            </div>
          </div>
          <div className="dialog-row-inputs">
            <InputSmall
              label="Drop Rate"
              type="per"
              per={true}
              value={inputRow.rate}
              onChange={(val) => handleInputChange("rate", val)}
              isError={errors.rate}
              placeholderText="10%"
            />

            <Input1
              label="Year"
              value={inputRow.year}
              type="customDate"
              onChange={(val) =>
                handleInputChange("year", val.replace(/\D/g, "").slice(0, 4))
              }
              isError={errors.year}
            />
            <AddButton
              isDisabled={!inputRow.year || !inputRow.rate}
              onClick={handleAdd}
            />
          </div>
          {dropRiskData.map((item, index) => (
            <div key={index}>
              <div className="no-dovider-dialogBox" />
              <div className="PotentialDropRisk-row">
                <InputSmall
                  value={item.rate}
                  type="per"
                  per="true"
                  // readOnly={true}
                  onChange={(val) => {
                    const updated = [...dropRiskData];
                    updated[index].rate = val;
                    dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
                  }}
                />
                <Input1
                  value={item.year}
                  onChange={(val) => {
                    const updated = [...dropRiskData];
                    updated[index].year = val.replace(/\D/g, "").slice(0, 4);
                    dispatch({ type: "SET_DROPRISK_DATA", payload: updated });
                  }}
                />

                <RemoveButton onClick={() => handleRemove(index)} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketAssumptionsDialogBox;
